/**
 * Formatting Utilities
 *
 * Functions for formatting view counts, timestamps, and other display values.
 */

/**
 * Formats a number as a compact view count string
 * @param count The number to format
 * @returns Formatted string like "1.2K views", "3.4M views"
 */
export const formatViewCount = (count: number | undefined): string => {
  if (!count || count === 0) return '0 views';

  const formatter = new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  });

  return `${formatter.format(count)} views`;
};

/**
 * Calculates and formats the time elapsed since a given date
 * @param date The date to calculate from
 * @returns Formatted string like "2 hours ago", "3 days ago"
 */
export const timeSince = (date: Date | string): string => {
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  // Handle future dates
  if (diffInSeconds < 0) return 'just now';

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'week', seconds: 604800 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(diffInSeconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count !== 1 ? 's' : ''} ago`;
    }
  }

  return 'just now';
};

/**
 * Formats a duration string (e.g., "PT4M13S" -> "4:13")
 * @param duration ISO 8601 duration string
 * @returns Formatted duration string
 */
export const formatDuration = (duration?: string): string => {
  if (!duration) return '';

  // Parse ISO 8601 duration (PT4M13S format)
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return duration;

  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Formats a channel title for display, handling long names
 * @param title The channel title
 * @param maxLength Maximum length before truncation
 * @returns Truncated title with ellipsis if needed
 */
export const formatChannelTitle = (title: string, maxLength: number = 50): string => {
  if (title.length <= maxLength) return title;
  return `${title.slice(0, maxLength - 3)}...`;
};

/**
 * Formats a video title for display, handling long names
 * @param title The video title
 * @param maxLength Maximum length before truncation
 * @returns Truncated title with ellipsis if needed
 */
export const formatVideoTitle = (title: string, maxLength: number = 80): string => {
  if (title.length <= maxLength) return title;
  return `${title.slice(0, maxLength - 3)}...`;
};
